import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { GiBee } from "react-icons/gi";
import { Page } from "../components/general/Page";
import { PageHeader } from "../components/general/PageHeader";
import { fetchProducts } from "../services";
import { ProductSection } from "../components/products/ProductSection";
import { CategoriesBar } from "../components/products/CategoriesBar";
import { ProductsSkeleton } from "../components/products/ProductsSkeleton";
import { colors, easing, fadeIn, layout } from "../theme";
import { getCategoriesWithTranslations, scrollBehavior } from "../utils";
import { useLanguage } from "../contexts/LanguageContext";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, currentLanguage } = useLanguage();
  const barRef = useRef(null);
  const listRef = useRef(null);
  const selectedCategory = searchParams.get("category");

  useEffect(() => {
    let isCancelled = false;
    setLoading(true);
    fetchProducts(currentLanguage)
      .then((response) => {
        if (!isCancelled) setProducts(response);
      })
      .catch((error) => console.log(error))
      .finally(() => {
        if (!isCancelled) setLoading(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [currentLanguage]);

  const categories = useMemo(() => getCategoriesWithTranslations(t), [t]);

  const sections = useMemo(
    () =>
      categories
        .filter(
          (category) =>
            !selectedCategory || category.value === selectedCategory
        )
        .map((category) => ({
          ...category,
          products: products.filter(
            (product) => product.category === category.value
          ),
        }))
        .filter((section) => section.products.length > 0),
    [categories, products, selectedCategory]
  );

  // When the sticky bar is used further down the page, bring the start of the
  // (now different) list back into view.
  const scrollListIntoView = () => {
    if (!barRef.current || !listRef.current) return;
    const barBottom = barRef.current.getBoundingClientRect().bottom;
    const listTop = listRef.current.getBoundingClientRect().top;
    if (listTop < barBottom) {
      window.scrollTo({
        top: window.scrollY + listTop - barBottom,
        behavior: scrollBehavior(),
      });
    }
  };

  const handleCategorySelect = (categoryValue) => {
    if (!categoryValue || categoryValue === selectedCategory) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ category: categoryValue });
    }
    scrollListIntoView();
  };

  const renderContent = () => {
    if (loading) return <ProductsSkeleton label={t("PRODUCTS.LOADING")} />;
    if (sections.length === 0) {
      return (
        <EmptyState>
          <GiBee />
          <p>{t("PRODUCTS.EMPTY")}</p>
        </EmptyState>
      );
    }
    return (
      <SectionsFade key={selectedCategory || "all"}>
        {sections.map((section) => (
          <ProductSection
            key={section.value}
            title={section.title}
            products={section.products}
          />
        ))}
      </SectionsFade>
    );
  };

  return (
    <Page>
      <PageHeader
        eyebrow={t("HOME.HEADER.TITLE")}
        title={t("HOME.OUR_PRODUCTS")}
        subtitle={t("HOME.QUICK_LINKS_TITLE")}
      />
      <CategoriesBar
        ref={barRef}
        categories={categories}
        selected={selectedCategory}
        allLabel={t("PRODUCTS.ALL")}
        onSelect={handleCategorySelect}
      />
      <ProductsContent ref={listRef}>{renderContent()}</ProductsContent>
    </Page>
  );
};

const ProductsContent = styled.div`
  width: 100%;
  max-width: ${layout.maxWidth};
  min-height: 60vh;
  margin: 0 auto;
  padding: 1rem ${layout.gutter} clamp(4rem, 8vw, 6rem);
`;

const SectionsFade = styled.div`
  animation: ${fadeIn} 0.5s ${easing.out} both;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 5rem 1rem;
  text-align: center;
  color: ${colors.textMuted};
  animation: ${fadeIn} 0.5s ease both;

  svg {
    font-size: 3rem;
    color: ${colors.primary};
  }
`;

export default Products;
